<template>
  <div class="admin-page admin-page-panel">
    <div class="admin-filter-bar">
      <a-select v-model="query.eventId" placeholder="选择赛事" allow-clear style="width:240px; margin-right:10px;" @change="handleEventChange">
        <a-select-option v-for="item in eventOptions" :key="item.id" :value="item.id">{{ item.title }}</a-select-option>
      </a-select>
      <a-select v-model="query.categoryId" placeholder="选择组别" allow-clear style="width:200px; margin-right:10px;">
        <a-select-option v-for="item in categoryOptions" :key="item.id" :value="item.id">{{ item.categoryName }}</a-select-option>
      </a-select>
      <a-button type="primary" icon="search" @click="handleSearch">查询</a-button>
      <a-button type="primary" icon="plus" style="margin-left:10px;" @click="handleAdd">新增 Race</a-button>
    </div>

    <a-table :columns="columns" :data-source="data" rowKey="id" :loading="loading" :pagination="pagination" @change="handleTableChange">
      <span slot="eventTitle" slot-scope="text, record">{{ getEventTitle(record.eventId) }}</span>
      <span slot="categoryName" slot-scope="text, record">{{ getCategoryName(record.categoryId) }}</span>
      <span slot="status" slot-scope="text"><a-tag :color="statusColor(text)">{{ statusLabel(text) }}</a-tag></span>
      <span class="admin-row-actions" slot="action" slot-scope="text, record">
        <a @click="handleEdit(record)">编辑</a>
        <a-divider type="vertical" />
        <a-popconfirm title="仅未锁定的 Race 可删除；后续草稿會自動補齊連續編號。" @confirm="handleDelete(record.id)"><a style="color:red;">删除</a></a-popconfirm>
      </span>
    </a-table>

    <a-modal v-model="visible" :title="form.id ? '编辑 Race' : '新增 Race'" @ok="handleOk" width="680px" destroyOnClose :confirm-loading="saving" :closable="!saving" :mask-closable="!saving" :keyboard="!saving" :cancel-button-props="{ props: { disabled: saving } }">
      <a-form-model ref="formRef" :model="form" :rules="rules" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
        <a-form-model-item label="所属赛事" prop="eventId">
          <a-select v-model="form.eventId" :disabled="saving || !!form.id" placeholder="请选择赛事" @change="handleFormEventChange">
            <a-select-option v-for="item in eventOptions" :key="item.id" :value="item.id">{{ item.title }}</a-select-option>
          </a-select>
        </a-form-model-item>
        <a-form-model-item label="所属组别" prop="categoryId">
          <a-select v-model="form.categoryId" :disabled="saving" placeholder="请选择组别">
            <a-select-option v-for="item in formCategoryOptions" :key="item.id" :value="item.id">{{ item.categoryName }}</a-select-option>
          </a-select>
        </a-form-model-item>
        <a-form-model-item label="Race No."><a-input-number v-model="form.raceNo" :min="1" disabled placeholder="由系統按賽事連續編號" style="width:100%;" /></a-form-model-item>
        <single-source-content v-if="visible" ref="roundContent" :value="form" :fields="roundContentFields" kind="round" :event-id="form.eventId" />
        <a-form-model-item :label="$t('stageType')">
          <a-select v-model="form.stageCode" :disabled="saving"><a-select-option v-for="item in stageCodes" :key="item" :value="item">{{ stageLabel(item) }}</a-select-option></a-select>
        </a-form-model-item>
        <a-form-model-item label="組內輪次"><a-input-number v-model="form.heatNo" :min="1" style="width:100%;" /></a-form-model-item>
        <a-form-model-item label="内部编码"><a-input v-model="form.roundCode" placeholder="可选" /></a-form-model-item>
        <a-form-model-item label="轮次顺序"><a-input-number v-model="form.roundOrder" :min="0" style="width:100%;" /></a-form-model-item>
        <a-form-model-item label="轮次时间"><a-date-picker v-model="form.roundTime" show-time style="width:100%;" value-format="YYYY-MM-DD HH:mm:ss" /></a-form-model-item>
        <a-form-model-item label="状态">
          <a-radio-group v-model="form.status"><a-radio :value="0">未开始</a-radio><a-radio :value="1">进行中</a-radio><a-radio :value="2">已结束</a-radio><a-radio :value="3">已取消</a-radio></a-radio-group>
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import { deleteDragonRound, getDragonCategoryList, getDragonEventList, getDragonRoundList, saveDragonRound } from '@/api'
import SingleSourceContent from '@/components/SingleSourceContent.vue'
import { createContentDraft, contentPayload, ROUND_CONTENT_FIELDS } from '@/utils/single-source-content'

export default {
  components: { SingleSourceContent },
  data() {
    return {
      query: { eventId: undefined, categoryId: undefined },
      eventOptions: [], categoryOptions: [], formCategoryOptions: [], data: [], loading: false, visible: false, saving: false, disposed: false,
      roundContentFields: ROUND_CONTENT_FIELDS,
      pagination: { current: 1, pageSize: 10, total: 0, showSizeChanger: true },
      form: { status: 0, roundOrder: 0, stageCode: 'HEAT', heatNo: 1 },
      rules: {
        eventId: [{ required: true, message: '请选择赛事' }], categoryId: [{ required: true, message: '请选择组别' }]
      },
      stageCodes: ['HEAT', 'REPECHAGE', 'QUARTERFINAL', 'SEMIFINAL', 'FINAL', 'CLASSIFICATION', 'OTHER'],
      columns: [
        { title: '所属赛事', key: 'eventTitle', scopedSlots: { customRender: 'eventTitle' } },
        { title: '所属组别', key: 'categoryName', scopedSlots: { customRender: 'categoryName' } },
        { title: 'Race', dataIndex: 'raceNo', width: 80, customRender: value => value ? `R${value}` : '-' },
        { title: '轮次名称', dataIndex: 'roundName' }, { title: '階段碼', dataIndex: 'stageCode', width: 130 },
        { title: '組內輪次', dataIndex: 'heatNo', width: 90 }, { title: '轮次时间', dataIndex: 'roundTime', width: 170 },
        { title: '状态', dataIndex: 'status', width: 100, scopedSlots: { customRender: 'status' } },
        { title: '操作', key: 'action', width: 160, scopedSlots: { customRender: 'action' } }
      ]
    }
  },
  created() { this.loadEvents(); this.loadData() },
  beforeDestroy() { this.disposed = true },
  methods: {
    stageLabel(code) {
      const labels = { HEAT: ['预赛', '預賽', 'Heat'], REPECHAGE: ['复活赛', '復活賽', 'Repechage'], QUARTERFINAL: ['四分之一决赛', '四分之一決賽', 'Quarterfinal'], SEMIFINAL: ['半决赛', '準決賽', 'Semifinal'], FINAL: ['决赛', '決賽', 'Final'], CLASSIFICATION: ['排位赛', '排位賽', 'Classification'], OTHER: ['其他', '其他', 'Other'] }
      return (labels[code] || labels.OTHER)[this.$adminLocale === 'en' ? 2 : this.$adminLocale === 'zh-Hant' ? 1 : 0]
    },
    loadEvents() { getDragonEventList({ page: 1, pageSize: 200 }).then(res => { this.eventOptions = (res.data && res.data.list) || [] }) },
    loadCategories(eventId, target = 'categoryOptions') {
      if (!eventId) { this[target] = []; return }
      getDragonCategoryList({ eventId }).then(res => { this[target] = (res.data && res.data.list) || [] })
    },
    getEventTitle(eventId) { const item = this.eventOptions.find(event => event.id === eventId); return item ? item.title : '' },
    getCategoryName(categoryId) { const item = [...this.categoryOptions, ...this.formCategoryOptions].find(category => category.id === categoryId); return item ? item.categoryName : '' },
    statusColor(status) { return status === 1 ? 'blue' : status === 2 ? 'green' : 'default' },
    statusLabel(status) { return status === 1 ? '进行中' : status === 2 ? '已结束' : status === 3 ? '已取消' : '未开始' },
    handleEventChange(eventId) { this.query.categoryId = undefined; this.loadCategories(eventId) },
    handleFormEventChange(eventId) { this.form.categoryId = undefined; this.loadCategories(eventId, 'formCategoryOptions') },
    handleSearch() { this.pagination.current = 1; this.loadData() },
    handleTableChange(pagination) { this.pagination.current = pagination.current; this.pagination.pageSize = pagination.pageSize; this.loadData() },
    loadData() {
      this.loading = true
      getDragonRoundList({ page: this.pagination.current, pageSize: this.pagination.pageSize, eventId: this.query.eventId, categoryId: this.query.categoryId })
        .then(res => { this.data = (res.data && res.data.list) || []; this.pagination.total = (res.data && res.data.total) || 0 })
        .finally(() => { this.loading = false })
    },
    handleAdd() {
      this.form = createContentDraft({ eventId: this.query.eventId, categoryId: this.query.categoryId, status: 0, roundOrder: 0, raceNo: undefined, stageCode: 'HEAT', heatNo: 1 }, ROUND_CONTENT_FIELDS)
      this.formCategoryOptions = []; if (this.query.eventId) this.loadCategories(this.query.eventId, 'formCategoryOptions'); this.visible = true
    },
    handleEdit(record) { this.form = createContentDraft(record, ROUND_CONTENT_FIELDS); this.formCategoryOptions = []; this.loadCategories(record.eventId, 'formCategoryOptions'); this.visible = true },
    handleOk() {
      if (this.saving || this.disposed) return
      this.$refs.formRef.validate(async valid => {
        if (!valid || this.saving) return
        this.saving = true
        try {
          await this.$refs.roundContent.prepare()
          if (this.disposed) return
          await saveDragonRound(contentPayload(this.form))
          if (this.disposed) return
          this.$message.success('保存成功'); this.visible = false; this.loadData()
        } catch (error) { if (!this.disposed) this.$message.error(error.message) }
        finally { this.saving = false }
      })
    },
    handleDelete(id) { deleteDragonRound({ id }).then(() => { this.$message.success('删除成功'); this.loadData() }) }
  }
}
</script>
